/**
 * SPRINT 5B: Address Autocomplete Component
 * 
 * Features:
 * - Debounced search as user types
 * - Candidate address suggestions
 * - Structured address data extraction
 * - Fallback to manual input if API fails
 * 
 * Uses Nominatim (OpenStreetMap) - FREE, no API key required
 * For production, consider Google Places API or Mapbox for better results
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, MapPin, Loader2, X, AlertCircle, ChevronDown } from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';

// Address structure
export interface AddressData {
  fullText: string;
  line1: string;
  line2?: string;
  barangay?: string;
  city?: string;
  province?: string;
  postalCode?: string;
  country?: string;
  lat: number;
  lng: number;
  placeId?: string;
  provider: 'nominatim' | 'manual';
}

interface NominatimResult {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
  address?: {
    house_number?: string;
    road?: string;
    suburb?: string;
    neighbourhood?: string;
    village?: string;
    town?: string;
    city?: string;
    municipality?: string;
    county?: string;
    state?: string;
    region?: string;
    postcode?: string;
    country?: string;
    country_code?: string;
  };
  type?: string;
  importance?: number;
}

interface AddressAutocompleteProps {
  value?: string;
  onChange: (address: AddressData | null) => void;
  onInputChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  countryCode?: string; // Limit search to country (e.g., 'ph' for Philippines)
  viewbox?: string; // Limit search to bounding box
  required?: boolean;
  error?: string;
}

// Nominatim API endpoint (free, but rate-limited - 1 req/sec)
const NOMINATIM_API = 'https://nominatim.openstreetmap.org/search';

export const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  value = '',
  onChange,
  onInputChange,
  placeholder = 'Search for your address...',
  className = '',
  disabled = false,
  countryCode = 'ph',
  viewbox,
  required = false,
  error,
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState<NominatimResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debouncedSearch = useDebounce(inputValue, 400);

  // Search for addresses
  const searchAddresses = useCallback(async (query: string) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    setSearchError(null);

    try {
      const params = new URLSearchParams({
        q: query,
        format: 'json',
        addressdetails: '1',
        limit: '5',
        countrycodes: countryCode,
      });

      if (viewbox) {
        params.append('viewbox', viewbox);
        params.append('bounded', '1');
      }

      const response = await fetch(`${NOMINATIM_API}?${params}`, {
        headers: {
          'Accept-Language': 'en',
          'User-Agent': 'FrogBasket-App/1.0',
        },
      });

      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`);
      }

      const data: NominatimResult[] = await response.json();
      setSuggestions(data);
      setIsOpen(data.length > 0);
      setSelectedIndex(-1);
    } catch (err) {
      console.error('Address search error:', err);
      setSearchError('Search unavailable. You can type your address manually.');
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, [countryCode, viewbox]);

  // Trigger search when debounced value changes
  useEffect(() => {
    if (debouncedSearch) {
      searchAddresses(debouncedSearch);
    } else {
      setSuggestions([]);
      setIsOpen(false);
    }
  }, [debouncedSearch, searchAddresses]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onInputChange?.(newValue);
    
    if (!newValue) {
      onChange(null);
      setSuggestions([]);
      setIsOpen(false);
    }
  };

  // Convert Nominatim result to AddressData
  const parseNominatimResult = (result: NominatimResult): AddressData => {
    const addr = result.address || {};
    
    // Build line1 from house number and road
    const line1Parts = [addr.house_number, addr.road].filter(Boolean);
    const line1 = line1Parts.join(' ') || result.display_name.split(',')[0];
    
    // Find city (can be in different fields)
    const city = addr.city || addr.town || addr.municipality || addr.village || '';
    
    // Find barangay (suburb or neighbourhood in PH context)
    const barangay = addr.suburb || addr.neighbourhood || '';
    
    return {
      fullText: result.display_name,
      line1,
      line2: '',
      barangay,
      city,
      province: addr.state || addr.region || addr.county || '',
      postalCode: addr.postcode || '',
      country: addr.country || '',
      lat: parseFloat(result.lat),
      lng: parseFloat(result.lon),
      placeId: result.place_id.toString(),
      provider: 'nominatim',
    };
  };

  // Handle suggestion selection
  const handleSelect = (result: NominatimResult) => {
    const addressData = parseNominatimResult(result);
    setInputValue(addressData.fullText);
    onChange(addressData);
    setIsOpen(false);
    setSuggestions([]);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          handleSelect(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Clear input
  const handleClear = () => {
    setInputValue('');
    onChange(null);
    setSuggestions([]);
    setIsOpen(false);
    inputRef.current?.focus();
  };

  // Manual entry (fallback)
  const handleManualEntry = () => {
    if (inputValue.trim()) {
      onChange({
        fullText: inputValue,
        line1: inputValue,
        lat: 0, // Will need to be set via map pin
        lng: 0,
        provider: 'manual',
      });
      setIsOpen(false);
    }
  };

  return (
    <div ref={containerRef} className={`address-autocomplete relative ${className}`}>
      {/* Input Field */}
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
          {isLoading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Search className="w-5 h-5" />
          )}
        </div>
        
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setIsOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          className={`
            w-full pl-10 pr-10 py-3 
            bg-white dark:bg-gray-800 
            border rounded-lg
            ${error ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}
            focus:outline-none focus:ring-2 
            ${error ? 'focus:ring-red-500' : 'focus:ring-emerald-500'}
            transition-colors
            disabled:opacity-50 disabled:cursor-not-allowed
          `}
          autoComplete="off"
        />

        {/* Clear button */}
        {inputValue && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Error message */}
      {error && (
        <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
          <AlertCircle className="w-4 h-4" />
          {error}
        </p>
      )}

      {/* Search error / fallback */}
      {searchError && (
        <div className="mt-2 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-center justify-between">
          <span className="text-sm text-amber-700 dark:text-amber-300">{searchError}</span>
          <button
            type="button"
            onClick={handleManualEntry}
            className="text-sm text-amber-600 hover:text-amber-700 dark:text-amber-400 font-medium"
          >
            Use anyway
          </button>
        </div>
      )}

      {/* Suggestions Dropdown */}
      {isOpen && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-72 overflow-y-auto">
          {suggestions.map((result, index) => (
            <button
              key={result.place_id}
              type="button"
              onClick={() => handleSelect(result)}
              className={`
                w-full px-4 py-3 text-left flex items-start gap-3 
                hover:bg-gray-50 dark:hover:bg-gray-700 
                transition-colors border-b border-gray-100 dark:border-gray-700 last:border-b-0
                ${selectedIndex === index ? 'bg-emerald-50 dark:bg-emerald-900/20' : ''}
              `}
            >
              <MapPin className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  {result.display_name.split(',')[0]}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {result.display_name.split(',').slice(1).join(', ').trim()}
                </p>
              </div>
            </button>
          ))}
          
          {/* Manual entry option */}
          <button
            type="button"
            onClick={handleManualEntry}
            className="w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 text-sm"
          >
            <ChevronDown className="w-5 h-5" />
            Use "{inputValue}" as entered
          </button>
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;

